import { Button } from '@factorialco/deprecated-design-system'
import {
  PayrollConceptFragment,
  ReimbursablePayrollConceptFragment,
  TimeSettingsCustomTimeRangeCategoryFragment,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { timeRangeCategoryTypeEnum } from 'modules/attendance/lib/enums'
import MapTimeCategoryWithPayrollConcept from 'modules/compensations/components/MapTimeCategoryWithPayrollConcept'
import useUpsertReimbursablePayrollConcept from 'modules/compensations/hooks/useUpsertReimbursablePayrollConcept'

interface Props {
  reimbursements: ReimbursablePayrollConceptFragment[]
  payrollConcepts: PayrollConceptFragment[]
  timeRangeCategories: TimeSettingsCustomTimeRangeCategoryFragment[]
  onCancel: () => void
  onSubmit: () => void
}

const PayrollConceptMapping = ({
  reimbursements,
  payrollConcepts,
  timeRangeCategories,
  onCancel,
  onSubmit,
}: Props) => {
  const { upsert: upsertMapping } = useUpsertReimbursablePayrollConcept()

  const handleMappingChange = async (timeCategoryId: number, payrollConceptId: number) => {
    const reimbursement = reimbursements.find((r) => r.reimbursableId === timeCategoryId)

    const result = await upsertMapping({
      id: reimbursement?.id,
      payrollConceptId,
      reimbursableId: timeCategoryId,
      reimbursableType: timeRangeCategoryTypeEnum.TimeSettingsCustomTimeRangeCategory,
    })

    const { success } = handleResult(result, () => undefined)

    if (success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.error'),
      })
    }
  }

  const allCategoriesMapped = timeRangeCategories.every(
    (category) => reimbursements.find((r) => r.reimbursableId === category.id)?.payrollConceptId
  )

  const renderFooter = () => (
    <>
      <Button type="secondary" onClick={onCancel}>
        {i18n.t('common.cancel')}
      </Button>
      <Button submit disabled={!allCategoriesMapped}>
        {i18n.t('common.continue')}
      </Button>
    </>
  )

  return (
    <Modal
      illustration="money"
      title={i18n.t(
        'time_tracking.modals.compensation.map_time_category_with_payroll_concept.title'
      )}
      onClose={onCancel}
      onSubmit={onSubmit}
      footer={renderFooter()}
    >
      <MapTimeCategoryWithPayrollConcept
        reimbursements={reimbursements}
        payrollConcepts={payrollConcepts}
        timeRangeCategories={timeRangeCategories}
        onRowChanged={handleMappingChange}
      />
    </Modal>
  )
}

export default PayrollConceptMapping
